import { useEffect, useState } from "react";
import { AVATAR_BUCKET, qubbase } from "@/lib/qubbase";

const cache = new Map<string, { url: string; expires: number }>();
const pending = new Map<string, Promise<string | null>>();
const TTL_SECONDS = 3600;

/** A signed URL for a file in the private avatars bucket (cached until shortly before it expires). */
async function signedUrl(path: string): Promise<string | null> {
  const hit = cache.get(path);
  if (hit && hit.expires > Date.now() + 60_000) return hit.url;
  let p = pending.get(path);
  if (!p) {
    p = qubbase.storage
      .from(AVATAR_BUCKET)
      .createSignedUrl(path, TTL_SECONDS)
      .then(({ data }) => {
        pending.delete(path);
        if (!data) return null;
        cache.set(path, {
          url: data.signedUrl,
          expires: Date.now() + TTL_SECONDS * 1000,
        });
        return data.signedUrl;
      });
    pending.set(path, p);
  }
  return p;
}

export function useAvatarUrl(path: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(() =>
    path ? (cache.get(path)?.url ?? null) : null,
  );

  useEffect(() => {
    if (!path) return setUrl(null);
    let alive = true;
    void signedUrl(path).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [path]);

  return url;
}
